"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { I18nMessages } from "../../wrappers/I18n/utils/i18n-message-generator";
import { I18nWrapperProps } from "../../wrappers/I18n/I18nWrapper";

export interface Locale {
  code: string;
  name: string;
}

interface ContextProps {
  locale: string;
  locales: Locale[];
  changeLocale: (locale: string) => void;
  messages: I18nMessages;
}

interface I18nProviderProps extends I18nWrapperProps {
  locale: string;
  messages: I18nMessages;
}

const I18nContext = createContext<ContextProps>({
  locale: "",
  locales: [],
  changeLocale: () => {},
  messages: {},
});

export const useI18nContext = () => useContext(I18nContext);

export const useLocale = (): string => {
  const { locale } = useI18nContext();

  return locale;
};

export const useLocales = (): Locale[] => {
  const { locales } = useI18nContext();

  return locales;
};

export const useChangeLocale = () => {
  const { changeLocale } = useI18nContext();

  return changeLocale;
};

export const useMessages = () => {
  const { messages } = useI18nContext();

  return messages;
};

export default function I18nProvider({
  children,
  locales,
  locale,
  messages,
}: I18nProviderProps) {
  const localeCodes = locales.map((locale) => {
    return locale.code;
  });

  type LocaleType = (typeof localeCodes)[number];

  const changeLocale = (locale: LocaleType) => {
    if (!localeCodes.includes(locale)) {
      throw new Error("This locale code not found in locales array!");
    }
  };

  const contextData: ContextProps = {
    locale,
    locales,
    changeLocale,
    messages,
  };

  return (
    <I18nContext.Provider value={contextData}>{children}</I18nContext.Provider>
  );
}
